import * as React from "react";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  ContentContainer,
  Card,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";
import styled from "styled-components";

const FAQItem = styled.details`
  margin: 12px 0;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 4px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &[open] summary::after {
    transform: rotate(180deg);
  }
`;

const FAQQuestion = styled.summary`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  padding: 12px 0;
  list-style: none;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;

  &::-webkit-details-marker {
    display: none;
  }

  &::after {
    content: "\\25BC";
    font-size: 11px;
    transition: transform 0.3s ease;
    color: #999;
    flex-shrink: 0;
    margin-top: 4px;
  }
`;

const FAQAnswer = styled.div`
  padding: 0 0 12px 0;
  line-height: 1.7;
  color: #444;

  p {
    margin-bottom: 10px;
  }

  ul,
  ol {
    margin: 10px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 6px;
  }

  h4 {
    color: var(--primaryColor);
    margin: 16px 0 8px 0;
  }
`;

export const FAQRoute = React.memo(function FAQRoute() {
  const { t } = useTexts();

  return (
    <AppLayout>
      <ContentContainer>
        <h1>{t("faq.title", "Fragen und Antworten")}</h1>

        {/* Kategorie 1: Installation, Wartung und Zuständigkeiten */}
        <Card>
          <h2>
            {t("faq.cat1.title", "Installation, Wartung und Zuständigkeiten")}
          </h2>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat1.q1.title",
                "Für was ist der Hausbesitzer zuständig, was muss der Kunde selbst warten? Für was ist der Versorger zuständig?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat1.q1.text1",
                    "Die Verantwortung ist zweigeteilt: Bis zum Hauswasserzähler, also bis zur Übergabestelle in das Gebäude, ist der Wasserversorger dafür verantwortlich, dass die vorgeschriebenen Qualitätsparameter für die Trinkwasserqualität eingehalten werden. Für die Qualität des Trinkwassers in der Hausinstallation muss der Hauseigentümer selbst Sorge tragen. Auch für ihn gelten die Anforderungen der Trinkwasserverordnung. [1]"
                  )
                )}
              </p>
              <h4>
                {t(
                  "faq.cat1.q1.subtitle1",
                  "Zuständigkeit Wasserversorger (Hauptanschluss)"
                )}
              </h4>
              <ul>
                <li>
                  {t(
                    "faq.cat1.q1.li1",
                    "Leitung bis zur Wasseruhr: Der Versorger ist für die Versorgung bis zur Hauptübergabestelle (Wasserzähler) verantwortlich."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q1.li2",
                    "Druck/Qualität: Bei Problemen mit dem Wasserdruck (unter 2 bar) oder der Wasserqualität vor der Uhr ist der Versorger zuständig."
                  )}
                </li>
              </ul>
              <h4>
                {t(
                  "faq.cat1.q1.subtitle2",
                  "Zuständigkeit Vermieter (Inneninstallation)"
                )}
              </h4>
              <ul>
                <li>
                  {t(
                    "faq.cat1.q1.li3",
                    "Wasserleitungen: Alle Leitungen ab dem Wasserzähler in der Wand."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q1.li4",
                    "Armaturen und Geräte: Wasserhähne, Duschköpfe, Eckventile und feste Anschlüsse."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q1.li5",
                    "Wartung und Reparatur: Der Vermieter muss für einen funktionierenden Zustand sorgen und Schäden (z. B. Rohrbrüche) beheben."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q1.li6",
                    "Legionellenprüfung: Die regelmäßige Untersuchung ist Pflicht des Vermieters."
                  )}
                </li>
              </ul>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat1.q2.title",
                "Wer ist zuständig bei Problemen an meiner Inneninstallation - Vermieter oder Wasserversorger?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat1.q2.text1",
                  "Bei Problemen an der Inneninstallation (hinter dem Wasserzähler) ist grundsätzlich der Vermieter zuständig. Der Wasserversorger ist nur für die Leitungen bis zum Hauptwasserzähler verantwortlich."
                )}
              </p>
              <p>
                {t(
                  "faq.cat1.q2.text2",
                  "Mieter müssen Wasserhähne warten und bei Abwesenheit für Wasseraustausch sorgen (Vermeidung von Stagnation). Kleinere Reparaturen an Dingen, die dem direkten Zugriff des Mieters unterliegen (z. B. Wasserhahn), können laut Mietvertrag ebenfalls vom Mieter zu tragen sein. Bei einem Rohrbruch ist der Vermieter für die Beseitigung zuständig, dem Mieter steht eine Mietminderung zu."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat1.q3.title",
                "Mein Wasserverbrauch ist viel höher als sonst, das kann ich nicht verbraucht haben?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <h4>
                {t(
                  "faq.cat1.q3.subtitle1",
                  "Möglicher Defekt in der Hausinstallation"
                )}
              </h4>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat1.q3.text1",
                    "Ein Punkt, den man als erstes überprüfen sollte, ist ein möglicher Defekt in der Hausinstallation. So kann die Toilettenspülung durchlaufen, ein Ventil an der Heizung undicht oder eine Leitung im Garten gerissen sein. Bereits kleine Mengen, sogenannte Schleichmengen, die durch undichte Stellen kontinuierlich verloren gehen, können einen großen Einfluss auf den Jahresverbrauch haben. Vor allem, wenn es viele Wochen oder Monate unbemerkt bleibt. [2]"
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat1.q3.text2",
                  "Der Verbraucher hat die Möglichkeit, solche Defekte selbst festzustellen. Dafür müssen zunächst alle Wasserzapfstellen in der Hausinstallation geschlossen und Haushaltsgeräte wie Geschirrspüler oder Waschmaschine außer Betrieb genommen werden. Wenn man nun einen Blick auf die Wasseruhr wirft, sollte sich dort keine Bewegung zeigen. Gibt es dennoch Bewegung, ist das ein Hinweis auf einen verdeckten Wasserverbrauch. In diesem Fall sollte sofort ein Fachmann hinzugezogen werden."
                )}
              </p>
              <h4>{t("faq.cat1.q3.subtitle2", "Der Wasserzähler")}</h4>
              <p>
                {t(
                  "faq.cat1.q3.text3",
                  "Hat man alle Möglichkeiten ausgeschlossen, gibt es die Möglichkeit, den Zähler einer sogenannten \u201EBefundsprüfung\u201C nach dem Eichgesetz zu unterziehen. Dies erfolgt durch das unabhängige Landesamt für Mess- und Eichwesen (LME) und kann von Kundenseite aus jederzeit beantragt werden. In den letzten 20 Jahren wurde jedoch lediglich bei zwei von 400 bis 600 überprüften Zählern ein Defekt festgestellt. In diesen Fällen müssen die Kosten der Überprüfung vom Betroffenen selbst getragen werden, sofern kein Mangel am Wasserzähler festgestellt wurde."
                )}
              </p>
              <h4>
                {t(
                  "faq.cat1.q3.subtitle3",
                  "Hohe Rechnung durch Defekt und Wasserrohrbrüche"
                )}
              </h4>
              <p>
                {t(
                  "faq.cat1.q3.text4",
                  "Wasserversorger können bei Rohrbrüchen keine Ermäßigung der Rechnung gewähren, denn sie agieren im Sinne der Gerechtigkeit für die gesamte Kundschaft. Als Wasserversorger endet die Zuständigkeit an der Wasseruhr. Die Abgabe des Trinkwassers ist auch für die Versorger stets mit Kosten verbunden, unabhängig davon, ob es verwendet wurde oder 'davongelaufen' ist. Anders ist es beim Abwasserentsorger: Wenn das Wasser durch einen Defekt in der Gartenanlage nicht dem Kanalnetz zugeführt wurde, sondern im Boden versickert ist, kann man den Betroffenen entgegenkommen."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat1.q4.title",
                'So viel kann eine \u201Ehängengebliebene" Toilettenspülung doch gar nicht verbrauchen?'
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat1.q4.text1",
                  "Doch, eine hängengebliebene Toilettenspülung kann erhebliche Mengen an Wasser verschwenden – weit mehr, als die meisten Menschen vermuten. Es handelt sich hierbei um eine der häufigsten Ursachen für unerklärlich hohe Wasserrechnungen."
                )}
              </p>
              <ul>
                <li>
                  {t(
                    "faq.cat1.q4.li1",
                    "Verbrauch pro Tag: Eine defekte oder hängende Spülung kann problemlos mehrere hundert Liter Wasser pro Tag verbrauchen. Manche Quellen sprechen von bis zu ca. 500–750 Liter pro Tag."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q4.li2",
                    "Verbrauch pro Monat: Wenn der Defekt nicht sofort bemerkt wird, können über einen Monat hinweg schnell 15.000 Liter (15 Kubikmeter) oder mehr verschwendet werden."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q4.li3",
                    "Kosten: Bei einem durchschnittlichen Wasserpreis (Trink- und Abwasser) können bei 500 Litern pro Tag schnell über 2 Euro Verlust täglich zusammenkommen. Ein monatelanges Leck kann zu einer Rechnung im hohen dreistelligen oder vierstelligen Bereich führen."
                  )}
                </li>
              </ul>
              <p>
                {t(
                  "faq.cat1.q4.text2",
                  "Das Wasser fließt oft unauffällig und relativ leise über das Überlaufrohr im Spülkasten oder eine undichte Dichtung direkt in die Schüssel und von dort in den Abfluss."
                )}
              </p>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat1.q4.text3",
                    "**So prüfen Sie es:** Geben Sie etwas Lebensmittelfarbe oder einen Färbestab in den Spülkasten. Wenn sich das Wasser in der Schüssel nach 10–15 Minuten verfärbt, ohne dass Sie gespült haben, ist die Spülung undicht."
                  )
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat1.q5.title",
                "Kann der Wasserdruck bei mir erhöht werden?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat1.q5.text1",
                    "Wenn Ihre Hausinstallation dem aktuellen Stand der Technik entspricht, durchläuft das ins Haus fließende Trinkwasser hinter dem Wasserzähler einen sogenannten Feinfilter. Durch ihn sollen Partikel wie Sand oder kleinste Partikelablagerungen aus den Rohrleitungen zurückgehalten werden. Andernfalls können sich die Partikel in der Installation ablagern und zur Korrosion führen, die Dichtungen in den Armaturen beschädigen oder das Wasser eintrüben. [3]"
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat1.q5.text2",
                  "Bei den Filtern gibt es grundsätzlich zwei Ausführungen: Rückspülbare und nicht rückspülbare Filter, die entweder alle 2 Monate gespült (rückspülbare Filter) oder alle 6 Monate gewechselt (nicht rückspülbare Filter) werden müssen. So ist gewährleistet, dass es nicht zum Druckabfall in der Hausinstallation kommt oder die Beschaffenheit des Trinkwassers beeinträchtigt wird."
                )}
              </p>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat1.q5.text3",
                    "**Hinweis:** Für die Reinigung der Filter ist der Hauseigentümer verantwortlich. Daher wird in solchen Fällen die Beauftragung eines Installateurs durch den Kunden/Hauseigentümer empfohlen."
                  )
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat1.q6.title",
                "Warum wurde ich nicht informiert, dass das Wasser heute aufgrund einer Störung abgestellt wurde?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat1.q6.text1",
                  "Es ist verständlich, dass Kunden über unangekündigten Wasserausfall verärgert sind, besonders wenn sie für eine zuverlässige Versorgung bezahlen. Dennoch gibt es Gründe, warum eine Information im Vorfeld möglicherweise nicht erfolgt:"
                )}
              </p>
              <ul>
                <li>
                  {t(
                    "faq.cat1.q6.li1",
                    "Akute Störung/Notfall: Bei unvorhergesehenen Schäden, wie einem Rohrbruch, muss die Wasserversorgung sofort unterbrochen werden, um Folgeschäden zu vermeiden. In diesen Fällen ist eine vorherige Ankündigung oft technisch unmöglich."
                  )}
                </li>
              </ul>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat1.q6.text2",
                    "**Hinweis:** Es gibt weder eine gesetzliche Pflicht zur Vorabinformation, noch eine starre gesetzliche Regelung, wie lange vorher eine notwendige Abstellung angekündigt werden muss, auch wenn die Wasserversorger alles dafür tun, ihre Kunden im Vorfeld wenn möglich zu informieren."
                  )
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat1.q7.title",
                "Warum dauert die Baustelle vor meinem Haus so lange?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat1.q7.text1",
                  "Dass Baustellen lange dauern und oft stillzustehen scheinen, ist ein häufiges Ärgernis. Verzögerungen entstehen meist durch ein Zusammenspiel von fehlenden Kapazitäten, unvorhergesehenen Problemen und organisatorischen Mängeln."
                )}
              </p>
              <ul>
                <li>
                  {t(
                    "faq.cat1.q7.li1",
                    "Fachkräftemangel und knappe Ressourcen: Die Baubranche ist überlastet. Oft fehlen qualifizierte Handwerker."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q7.li2",
                    "Schlechte Koordination der Gewerke: Wenn verschiedene Firmen (Sanitär, Elektro, Straßenbau) nicht exakt aufeinander abgestimmt sind, entstehen lange Wartezeiten."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q7.li3",
                    "Unvorhergesehene Hindernisse: Besonders bei Tiefbauarbeiten stößt man oft auf Überraschungen im Boden, die nicht im Plan standen."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q7.li4",
                    "Materialengpässe: Lieferprobleme bei Baustoffen führen zu Zwangspausen."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q7.li5",
                    "Wetterabhängigkeit: Regen, Frost oder extreme Hitze machen bestimmte Arbeiten unmöglich oder verzögern das Trocknen von Beton."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q7.li6",
                    "Nachbesserungen und Baumängel: Wenn Arbeiten fehlerhaft ausgeführt wurden, müssen sie abgerissen und neu gemacht werden."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q7.li7",
                    "Bürokratische Verzögerungen: Genehmigungsverfahren oder fehlende Unterlagen können den Fortgang stark ausbremsen."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat1.q7.li8",
                    '\u201ESacken lassen": Bei Straßenbauarbeiten muss der Untergrund oft über Wochen hinweg \u201Esacken", bevor die finale Asphaltschicht aufgetragen werden kann.'
                  )}
                </li>
              </ul>
            </FAQAnswer>
          </FAQItem>
        </Card>

        {/* Kategorie 2: Wasserqualität */}
        <Card>
          <h2>{t("faq.cat2.title", "Wasserqualität")}</h2>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q1.title",
                "Ist unser Trinkwasser wirklich sauber und gesundheitlich unbedenklich?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat2.q1.text1",
                    "Die Qualität des Trinkwassers und dessen Überwachung wird in Deutschland in der Trinkwasserverordnung geregelt. Sie schreibt vor, welche Inhaltsstoffe in welcher Konzentration im Trinkwasser enthalten sein dürfen. Hiernach darf der Genuss oder Gebrauch von Trinkwasser die menschliche Gesundheit nicht durch Krankheitserreger oder chemische Stoffe gefährden. Es muss weiterhin rein und genusstauglich, also farblos, klar, geruchlos und ohne anormalen Geschmack sein. [1]"
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat2.q1.text2",
                  "Die Trinkwasserqualität wird regelmäßig im Rahmen der Eigenkontrollen von den Wasserversorgern selbst, aber auch durch die Gesundheitsbehörden, geprüft. Es gehört zu den am besten überwachten Lebensmitteln. Dabei wird immer wieder bestätigt, dass das Trinkwasser in Deutschland eine sehr gute Qualität hat und gesundheitlich unbedenklich ist."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q2.title",
                "Was steht in der Trinkwasserverordnung (TrinkwV)?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat2.q2.text1",
                  "Die Trinkwasserverordnung (TrinkwV) ist ein deutscher Rechtsakt, der sicherstellt, dass das Trinkwasser für Verbraucher gesundheitlich unbedenklich und von hoher Qualität ist."
                )}
              </p>
              <h4>{t("faq.cat2.q2.subtitle1", "Allgemeine Anforderungen")}</h4>
              <p>
                {t(
                  "faq.cat2.q2.text2",
                  "Trinkwasser muss für die menschliche Gesundheit unbedenklich sein. Die Verordnung legt spezifische Grenzwerte für eine Vielzahl von chemischen, biologischen und physikalischen Parametern fest, darunter Nitrate, Nitrite, Schwermetalle, Bakterien und Viren."
                )}
              </p>
              <h4>
                {t(
                  "faq.cat2.q2.subtitle2",
                  "Überwachungs- und Kontrollpflichten"
                )}
              </h4>
              <p>
                {t(
                  "faq.cat2.q2.text3",
                  "Wasserwerke sind verpflichtet, regelmäßig die Qualität des Trinkwassers zu testen. Dazu gehören sowohl routinemäßige als auch anlassbezogene Prüfungen. Bei den Kontrollen müssen Proben aus verschiedenen Quellen entnommen und analysiert werden."
                )}
              </p>
              <h4>
                {t("faq.cat2.q2.subtitle3", "Pflichten der Wasserversorger")}
              </h4>
              <p>
                {t(
                  "faq.cat2.q2.text4",
                  "Wasserversorger müssen die Ergebnisse ihrer Kontrollen dokumentieren und der Öffentlichkeit zugänglich machen. Verbraucher müssen über die Qualität ihres Trinkwassers und über etwaige Maßnahmen informiert werden, insbesondere im Falle von gesundheitlichen Risiken."
                )}
              </p>
              <h4>
                {t(
                  "faq.cat2.q2.subtitle4",
                  "Mikrobiologische und chemische Anforderungen"
                )}
              </h4>
              <p>
                {t(
                  "faq.cat2.q2.text5",
                  "Das Trinkwasser darf keine krankheitserregenden Mikroorganismen enthalten. Die Verordnung hat klare Festlegungen zu maximalen Konzentrationen von Schadstoffen wie Blei, Kupfer, Zink, Nitrat und anderen chemischen Verunreinigungen."
                )}
              </p>
              <h4>{t("faq.cat2.q2.subtitle5", "Weitere Regelungen")}</h4>
              <p>
                {t(
                  "faq.cat2.q2.text6",
                  "Die Verordnung regelt außerdem zulässige Verfahren zur Trinkwasseraufbereitung, sanitärtechnische Anforderungen an Installation und Wartung von Trinkwasseranlagen, sowie Risikomanagement mit Präventions- und Notfallplänen. Sie wird regelmäßig aktualisiert, um neuen wissenschaftlichen Erkenntnissen und Europäischen Richtlinien Rechnung zu tragen."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q3.title",
                "Wasserqualität – was kann ich selbst tun?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat2.q3.text1",
                    "Das vom Wasserversorger bis zu den Verbrauchern gelieferte Trinkwasser unterliegt hohen Qualitätsstandards. Doch auch Sie können Ihren Beitrag dafür leisten, die Trinkwasserqualität in der Hausinstallation zu bewahren. [4]"
                  )
                )}
              </p>
              <ul>
                <li>
                  {t(
                    "faq.cat2.q3.li1",
                    "Entfernen Sie alte und ungenutzte Geräte (z. B. alte Wasserhähne und alte Spülbecken)."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat2.q3.li2",
                    "Lassen Sie Bestandteile Ihrer Hausinstallation, wie Wasserboiler, Filter und Enthärter, regelmäßig von einem Fachmann warten."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat2.q3.li3",
                    "Nutzen Sie beim Kochen und beim Zubereiten von Getränken kaltes statt warmes Wasser, da die Wärme in der Hausinstallation die Bakterienentstehung und -vermehrung begünstigen kann."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat2.q3.li4",
                    "Lassen Sie das Wasser am Morgen oder nach längerer Abwesenheit einen Moment lang laufen, damit das kühle Nass wieder frisch aus der Leitung fließt und die Leitungen durchgespült werden."
                  )}
                </li>
              </ul>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q4.title",
                "Ist Trinkwasser aus dem Wasserhahn qualitativ schlechter als Mineralwasser aus dem Handel?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat2.q4.text1",
                    "Nein. Im Gegensatz zu Mineralwasser kann Trinkwasser sogar in beliebig großen Mengen getrunken werden. Mineralwasser wird mit sehr unterschiedlichen Mineraliengehalten angeboten. Für gesunde Menschen ist das kein Problem. Bei bestimmten Krankheiten wie Bluthochdruck ist jedoch bei Mineralwasser auf einen niedrigen Natriumgehalt zu achten. Ein hoher Sulfatgehalt kann sogar abführend wirken und ist für Kinder nicht zu empfehlen. [5]"
                  )
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q5.title",
                "Kann ich das Trinkwasser für die Zubereitung von Baby- und Säuglingsnahrung bedenkenlos verwenden?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat2.q5.text1",
                    "Ja, absolut. Auch das Abkochen des Wassers ist nicht notwendig. Die Analyseergebnisse des Trinkwassers liegen meistens deutlich unter den gesetzlichen Grenzwerten oder sind gar nicht nachweisbar. Um stets frisches Trinkwasser zu verwenden, lassen Sie das Wasser zunächst einen Moment lang ablaufen, bis es merklich kühler ist. So fließt das abgestandene Wasser erst einmal weg und dann entnehmen Sie frisches Trinkwasser für Ihr Kind. [5]"
                  )
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q6.title",
                'Auf der Mineralwasserflasche steht \u201Egeeignet für die Herstellung von Babynahrung". Ist Mineralwasser also besser für Babys?'
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat2.q6.text1",
                    "Anbieter von Mineralwasser haben die Erlaubnis, zu Zwecken des Marketings diese Aussage auf das Etikett zu drucken, wenn das Wasser weniger als 10 mg Nitrat pro Liter enthält. Das ist eine reine Werbe-Aussage. Für Säuglingsnahrung empfiehlt die Weltgesundheitsorganisation (WHO) die Obergrenze von 25 mg Nitrat pro Liter. Die Qualität des Trinkwassers liegt meist deutlich darunter. Es ist also für den Menschen bestes Trinkwasser und auch für Babys und Kleinkinder geeignet. [5]"
                  )
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q7.title",
                "Welche Maßnahmen empfehlen Sie älteren Menschen, die gerne und viel Wasser trinken?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat2.q7.text1",
                  "Trinkwasser ist auch für ältere Menschen mit geschwächtem Immunsystem grundsätzlich das beste Getränk, um den Flüssigkeitsbedarf zu decken und einer Dehydrierung vorzubeugen. In Deutschland ist Leitungswasser von sehr hoher Qualität und kann meist bedenkenlos getrunken werden."
                )}
              </p>
              <ul>
                <li>
                  {t(
                    "faq.cat2.q7.li1",
                    "Abstehen des Wassers: Wenn Wasser lange in den Leitungen steht (Stagnationswasser), können sich Keime vermehren. Daher gilt: Wasser erst laufen lassen, bis es kühl aus der Leitung kommt."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat2.q7.li2",
                    "Alte Rohre: In Häusern mit sehr alten Bleirohren (vor 1973 verbaut) sollte das Wasser nicht getrunken werden."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat2.q7.li3",
                    "Legionellen: In Seniorenheimen oder älteren Gebäuden können sich Legionellen in der Warmwasseranlage vermehren. Kaltes Wasser ist hier unproblematischer als warmes."
                  )}
                </li>
              </ul>
              <p>
                {t(
                  "faq.cat2.q7.text2",
                  "Bei Unsicherheit über die Hausinstallation kann auf abgefülltes Mineralwasser zurückgegriffen werden, oder das Leitungswasser zur Sicherheit abgekocht werden."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q8.title",
                "Kann der Kalk im Trinkwasser die Gesundheit schädigen?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat2.q8.text1",
                  "Das Gegenteil ist der Fall: Mineralstoffe wie Calcium- und Magnesiumcarbonat sind sogar wichtig für den Körper."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t("faq.cat2.q9.title", "Wird unser Trinkwasser gechlort?")}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat2.q9.text1",
                  "In einigen Gewinnungsanlagen bzw. Wasserwerken wird eine Sicherheitschlorung durchgeführt. Die Chlorung erfolgt in Abstimmung mit dem Gesundheitsamt und dient dazu, das Verkeimungsrisiko des Trinkwassers auf dem Verteilungsweg zu minimieren. Zulässig nach der Trinkwasserverordnung sind 0,3 Cl₂/l. Ob Ihr Trinkwasser gechlort wird, können Sie in der für Ihren Wohnort maßgeblichen Trinkwasseranalyse nachsehen."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q10.title",
                "Wie häufig wird die Trinkwasserqualität geprüft? Wo werden die Ergebnisse veröffentlicht?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat2.q10.text1",
                    "Die Trinkwasserverordnung unterscheidet zwischen Pflichten der Wasserversorger und ihrer Überwachung. Die Gesundheitsämter überwachen die Wasserversorger. Am besten wenden Sie sich bei Fragen zunächst an Ihren Wasserversorger oder Hauseigentümer, und erst dann an Ihr örtliches Gesundheitsamt. [6]"
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat2.q10.text2",
                  "Die Trinkwasserverordnung verpflichtet die Wasserversorger regelmäßig zu überprüfen, ob alle Grenzwerte und Anforderungen eingehalten sind. Die Untersuchungshäufigkeit richtet sich nach der abgegebenen Trinkwassermenge: große Versorger müssen ihr Trinkwasser mehrmals täglich am Wasserwerksausgang und im Verteilungsnetz beproben, kleine Versorger je nach Größe seltener."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q11.title",
                "Welche Parameter sind für mich als Verbraucher hinsichtlich der Wasserqualität wichtig?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat2.q11.text1",
                    "Damit Menschen unbedenklich Wasser aus der Leitung trinken können, muss es frei von Schadstoffen und Krankheitserregern sein. Daher wird Grund-, Talsperren- oder Flusswasser durch verschiedene Aufbereitungsverfahren im Wasserwerk trinkbar gemacht. [7]"
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat2.q11.text2",
                  "Die Qualität von Leitungswasser wird durch die Trinkwasserverordnung (TrinkwV) geregelt. Alle Grenzwerte sind so niedrig festgelegt, dass es keinerlei Grund zur Sorge gibt. In Deutschland hält Leitungswasser die gesetzlichen Vorgaben zu über 99 Prozent ein."
                )}
              </p>
              <h4>
                {t(
                  "faq.cat2.q11.subtitle1",
                  "Wichtige Werte laut Trinkwasserverordnung"
                )}
              </h4>
              <ul>
                <li>
                  {t(
                    "faq.cat2.q11.li1",
                    "Mikrobiologie (E. coli, Enterokokken, coliforme Bakterien): Das Wasser muss frei von Krankheitserregern sein."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat2.q11.li2",
                    "Schwermetalle (Blei, Kupfer, Nickel): Blei ist besonders kritisch (Grenzwert ab 2028: 0,005 mg/l)."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat2.q11.li3",
                    "Nitrat/Nitrit: Höchstkonzentrationen für Nitrat liegen bei 50 mg/l, wichtig vor allem für Säuglingsnahrung."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat2.q11.li4",
                    "Pflanzenschutzmittel (Pestizide): Diese dürfen nur in extrem geringen Mengen (0,1 µg/l) enthalten sein."
                  )}
                </li>
              </ul>
              <h4>
                {t(
                  "faq.cat2.q11.subtitle2",
                  "Parameter für die Hausinstallation (Verbraucher-Verantwortung)"
                )}
              </h4>
              <ul>
                <li>
                  {t(
                    "faq.cat2.q11.li5",
                    "Blei in Rohren: In Häusern, die vor 1973 gebaut wurden, können noch Bleirohre vorhanden sein."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat2.q11.li6",
                    "Legionellen: Diese Bakterien vermehren sich in warmem (30–45 °C), stehendem Wasser. Wichtig ist eine ordnungsgemäße Warmwasseranlage."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat2.q11.li7",
                    "Stagnationswasser: Wenn Wasser lange in den Rohren steht, können sich Bakterien vermehren oder Metalle lösen."
                  )}
                </li>
              </ul>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q12.title",
                "Was sagen mir die Grenzwerte in der TrinkwV? Werde ich informiert, wenn diese nicht eingehalten werden?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat2.q12.text1",
                  "Die Grenzwerte in der Trinkwasserverordnung (TrinkwV) stellen sicher, dass das Leitungswasser in Deutschland einwandfrei und genusstauglich ist. Sie dienen als Höchstwerte für Schadstoffe, Keime und chemische Verbindungen, die bei lebenslangem Verzehr keine gesundheitlichen Beeinträchtigungen hervorrufen."
                )}
              </p>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat2.q12.text2",
                    "Falls Grenzwertüberschreitungen auftreten, hat das Erkennen und Beseitigen der Ursache Vorrang vor einer Symptombekämpfung. Das zuständige Gesundheitsamt prüft, ob die Überschreitung eine Gefahr für die Gesundheit bedeutet und unmittelbare Abhilfe erfordert oder ob sie vorübergehend duldbar ist. [8]"
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat2.q12.text3",
                  "Bei einer Überschreitung muss der Versorger oder der Eigentümer (bei Hausinstallationen) unverzüglich die Ursachen ermitteln und Abhilfe schaffen."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat2.q13.title",
                "Gibt es noch Blei im Trinkwasser? Wo kommt es her?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat2.q13.text1",
                    "Die deutsche Trinkwasserverordnung schreibt für Blei im Trinkwasser ab dem 1. Dezember 2013 einen Grenzwert von 0,010 Milligramm pro Liter vor. Das ist nur mit einer bleifreien Versorgungsleitung zu gewährleisten. Die Verantwortung dafür trägt der Hausbesitzer. [3]"
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat2.q13.text2",
                  "Das öffentliche Trinkwassernetz ist frei von Bleirohren. Allerdings kann das Wasser auf den letzten Metern vor Ihrem Wasserhahn – zum Beispiel in un- oder teilsanierten Gebäuden – durch alte Bleirohre fließen. Bis etwa 1970 wurde Blei als Installationsmaterial verarbeitet. Da Blei weich ist, können Sie freiliegende Bleileitungen gut erkennen: Sie lassen sich mit einem spitzen Gegenstand einritzen oder abschaben."
                )}
              </p>
              <p>
                {t(
                  "faq.cat2.q13.text3",
                  "Sind Bleirohre vorhanden, sollten diese schnellstmöglich ersetzt werden. Bis dahin wird Kindern und Schwangeren geraten, für Trink- und Nahrungszwecke abgepacktes Wasser zu verwenden. Es wird empfohlen, Leitungswasser aus Bleirohren vor der Nutzung ablaufen zu lassen, bis es merklich kühl geworden ist."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>
        </Card>

        {/* Kategorie 3: Wasserhärte und Wasserauffälligkeiten */}
        <Card>
          <h2>
            {t("faq.cat3.title", "Wasserhärte und Wasserauffälligkeiten")}
          </h2>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat3.q1.title",
                'Was bedeutet \u201Ehartes" oder \u201Eweiches" Wasser?'
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat3.q1.text1",
                    "Die Wasserhärte des Trinkwassers ist etwas ganz Natürliches und hängt davon ab, welche Bodenschichten das Wasser durchflossen hat: In Gebieten mit insbesondere kalkhaltigen Böden ist das Wasser härter. In Regionen mit Sandstein, Basalt, Schiefer oder Granit gibt es meist weicheres Wasser. Die im Wasser gelösten Calcium- und Magnesiumverbindungen bestimmen den Härtegrad des Wassers. [3]"
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat3.q1.text2",
                  "Härteres Wasser ist gesund, weil es lebenswichtige Mineralien enthält. So ist Calcium ein wichtiger Baustoff für Knochen und Zähne und unentbehrlich für die Blutgerinnung. Magnesium wirkt positiv auf die Nerven, die Muskelarbeit, die Arterien und kann einem Herzinfarkt vorbeugen. Deshalb gibt es für die Wasserhärte in der Trinkwasserverordnung auch keinen Grenzwert. Die Wasserhärte ist damit kein Qualitätsmerkmal."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat3.q2.title",
                "Welche Wasserhärte hat mein Trinkwasser?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat3.q2.text1",
                  "Die Wasserhärte in Ihrem Wohnort erfahren Sie am besten über die Website Ihres lokalen Wasserversorgers. Alternativ können Sie Teststreifen aus Apotheken oder Drogerien nutzen, um den Härtegrad (weich: unter 8,4 °dH, mittel: 8,4–14 °dH, hart: über 14 °dH) direkt aus der Leitung zu bestimmen."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat3.q3.title",
                "Das Wasser ist härter (schlechter) geworden, warum?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat3.q3.text1",
                  "Dass Wasser 'härter' geworden ist, bedeutet, dass der Gehalt an Calcium- und Magnesiumionen im Trinkwasser gestiegen ist. Dies führt zu mehr Kalkablagerungen in Haushaltsgeräten und erfordert oft eine höhere Dosierung von Waschmitteln."
                )}
              </p>
              <ul>
                <li>
                  {t(
                    "faq.cat3.q3.li1",
                    "Änderung der Wasserquelle: Wenn der Versorger mehr Wasser aus einem kalkhaltigen Gebiet einspeist, steigt die Härte. Dies kann saisonal oder durch Infrastrukturänderungen bedingt sein."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat3.q3.li2",
                    "Geologische Gegebenheiten: Hartes Wasser entsteht, wenn Regenwasser durch kalkhaltige Gesteinsschichten (Kalkstein, Gips, Kreide) versickert."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat3.q3.li3",
                    "Wetterlagen: Nach langen Trockenperioden kann die Wasserhärte schwanken."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat3.q3.li4",
                    "Technische Maßnahmen: Manchmal wird Wasser gezielt aufbereitet, um korrosionsbedingte Rohrleitungsschäden zu vermeiden, was die Härte lokal beeinflussen kann."
                  )}
                </li>
              </ul>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat3.q4.title",
                "Das Wasser riecht komisch – kann ich es trotzdem bedenkenlos trinken?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat3.q4.text1",
                  "Wenn Leitungswasser komisch riecht – insbesondere saisonal im Winter – ist dies meist ein Anzeichen für Ablagerungen in den Leitungen oder natürliche Prozesse. Eine Geruchsveränderung ist grundsätzlich ein Warnsignal, das geprüft werden sollte."
                )}
              </p>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat3.q4.text2",
                    "**Wahrscheinlich unbedenklich:** Wenn der Geruch erdig, modrig oder leicht fischig ist, liegt dies oft an natürlichen Stoffen wie Geosmin, die durch Algen entstehen. Ein leichter Chlorgeruch im Winter kann auftreten, da Wasserwerke die Desinfektion erhöhen."
                  )
                )}
              </p>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat3.q4.text3",
                    "**Vorsicht geboten:** Wenn das Wasser intensiv nach faulen Eiern (Schwefel), faulig oder chemisch riecht, sollten Sie es nicht trinken. Dies kann auf bakterielle Verunreinigungen oder Rückstände in den Rohren hindeuten."
                  )
                )}
              </p>
              <h4>{t("faq.cat3.q4.subtitle1", "Was Sie tun können")}</h4>
              <ul>
                <li>
                  {t(
                    "faq.cat3.q4.li1",
                    "Spülen: Lassen Sie das Wasser mehrere Minuten laufen, bis es kalt aus der Leitung kommt. Riecht es dann neutral, lag das Problem am Stagnationswasser."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat3.q4.li2",
                    "Andere Hähne prüfen: Wenn nur ein Hahn riecht, ist meist der Wasserhahn oder der Perlator (Sieb) verunreinigt. Reinigen Sie diesen."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat3.q4.li3",
                    "Nachbarn fragen: Tritt das Problem auch bei Nachbarn auf? Wenn ja, kontaktieren Sie Ihren lokalen Wasserversorger."
                  )}
                </li>
              </ul>
              <p>
                {t(
                  "faq.cat3.q4.text4",
                  "Wenn der Geruch nach kurzem Durchspülen nicht verschwindet, sollten Sie das Wasser sicherheitshalber nicht trinken und den Wasserversorger oder einen Installateur verständigen."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat3.q5.title",
                "Das Wasser ist trüb/milchig – ist das bedenklich?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat3.q5.text1",
                  "Trübes oder milchiges Wasser, das beim Einschenken weiß oder trüb aussieht und nach kurzer Zeit von unten nach oben wieder klar wird, ist in den allermeisten Fällen völlig unbedenklich. Die Trübung entsteht durch winzige Luftbläschen, die im Wasser gebunden sind. Es bestehen keine gesundheitlichen Risiken."
                )}
              </p>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat3.q5.text2",
                    "**Test:** Füllen Sie ein Glas Trinkwasser und lassen Sie es stehen. Wenn die Trübung innerhalb weniger Sekunden bis Minuten verschwindet (von unten nach oben), handelt es sich definitiv um Luft. Um den Effekt zu beheben, hilft es, den Perlator am Wasserhahn zu reinigen oder zu entkalken."
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat3.q5.text3",
                  "Wenn das Wasser nach einiger Zeit im Glas nicht klar wird, sollten Sie den Wasserversorger kontaktieren oder einen Wassertest durchführen lassen."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat3.q6.title",
                "Warum ist das Wasser braun/rostig, z. B. nach meinem Urlaub?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat3.q6.text1",
                  "Das Trinkwasser weist in seltenen Fällen eine bräunliche Färbung auf. Das ist gesundheitlich völlig unbedenklich, kann aber weiße Wäsche verfärben."
                )}
              </p>
              <h4>{t("faq.cat3.q6.subtitle1", "Abgestelltes Wasser")}</h4>
              <p>
                {t(
                  "faq.cat3.q6.text2",
                  "Bei Arbeiten am Rohrnetz oder längerer Abwesenheit steht Wasser in den Leitungen. Werden die Leitungen wieder benutzt, können sich Spuren von Rost von den Innenwänden lösen. Drehen Sie Ihre Wasserhähne voll auf und lassen Sie das Wasser ablaufen, bis die Braunfärbung verschwunden ist."
                )}
              </p>
              <h4>{t("faq.cat3.q6.subtitle2", "Hausinstallation")}</h4>
              <p>
                {t(
                  "faq.cat3.q6.text3",
                  "Ist in alten verzinkten Rohrleitungen die Zinkschicht 'verbraucht', kommt es zur Rostbildung. Dieser Effekt tritt außerdem auf, wenn entgegen den technischen Regeln ungeschützte Stahlrohre installiert wurden. In beiden Fällen sollten die Rohrleitungen durch ein im Installateurverzeichnis eingetragenes Installationsunternehmen ausgetauscht werden."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>
        </Card>

        {/* Kategorie 4: Wasseruntersuchung */}
        <Card>
          <h2>{t("faq.cat4.title", "Wasseruntersuchung")}</h2>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat4.q1.title",
                "Kann ich mein Wasser untersuchen lassen und was kostet das?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat4.q1.text1",
                  "Lokale Wasserversorger informieren auf ihren Seiten über die Wasserqualität, daher ist eine umfassende Beprobung des eigenen Trinkwassers (auch aus Kostengründen) nicht unbedingt notwendig."
                )}
              </p>
              <p>
                {t(
                  "faq.cat4.q1.text2",
                  "Dennoch lässt sich eine Beprobung durch Einsenden einer selbst genommenen Probe auf Blei, Kupfer, Legionellen und andere Stoffe für ca. 40–90 Euro bei spezialisierten Laboren durchführen. Alternativ bieten auch Apotheken Teststreifen an."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat4.q2.title",
                "Mein Wasser ist verschmutzt, das hat ein Anbieter von Wasseraufbereitung nachgewiesen"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat4.q2.text1",
                    "Wenn ein Wasseraufbereitungsanbieter eine Verschmutzung Ihres Trinkwassers nachgewiesen hat, ist Vorsicht geboten: In der Branche gibt es Berichte über unseriöse Anbieter, die mit angeblichen Schadstoffnachweisen unnötig teure Filteranlagen verkaufen wollen. So werden die Ergebnisse der Leitfähigkeit beispielsweise als Beleg für Schadstoffe verkauft. [11]"
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat4.q2.text2",
                  "Wenn Sie hinsichtlich der Qualität Ihres Leitungswassers verunsichert sind, können Sie sich an Ihren lokalen Wasserversorger wenden und gegebenenfalls ein Labor mit einer Wasseranalyse beauftragen. Filter sind in den meisten Fällen gar nicht notwendig, ja verschlechtern die Qualität des Wassers sogar oft."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>
        </Card>

        {/* Kategorie 5: Wasserfilter und Entkalkung */}
        <Card>
          <h2>{t("faq.cat5.title", "Wasserfilter und Entkalkung")}</h2>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat5.q1.title",
                "Brauche ich eine Wasserenthärtungsanlage oder einen Filter?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat5.q1.text1",
                    "Das Trinkwasser ist von höchster Qualität und gesund. Alle in der Trinkwasserverordnung festgesetzten Grenzwerte werden weit unterschritten. Unabhängige Verbraucherschützer von der Stiftung Warentest bis zur Verbraucherzentrale bestätigen das immer wieder. Eine zusätzliche Behandlung von Trinkwasser ist deshalb nicht notwendig. Eine Enthärtung kann lediglich erforderlich sein, um die Hausinstallation vor Kalk zu schützen. [3]"
                  )
                )}
              </p>
              <p>
                {t(
                  "faq.cat5.q1.text2",
                  "Sollten Sie sich trotzdem für den Einbau eines Filters entscheiden, sollten Sie nur einen mit DVGW-Zulassung verwenden. Beachten Sie unbedingt, dass Wasserfilter gut gepflegt werden müssen. Filter, die nur zeitweilig verwendet werden, können verkeimen."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat5.q2.title",
                "Müssen Wasserfilter regelmäßig gereinigt und gewechselt werden?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat5.q2.text1",
                    "Um einen sicheren und hygienischen Betrieb sicherzustellen, sollten Wasserfilter in regelmäßigen Abständen ausgetauscht werden. Die Verfahren und Zeitabstände sind den Herstelleranweisungen zu entnehmen. Nach DIN EN 806-5 ist aus hygienischen Gründen ein Austausch der Filterkerze oder die Durchführung einer Rückspülung bei Rückspülfiltern alle 6 Monate erforderlich. [9]"
                  )
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat5.q3.title",
                "Welche Haushaltsgeräte sollten regelmäßig entkalkt werden? Wo kommt der Kalk her?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat5.q3.text1",
                  "Regelmäßiges Entkalken ist essenziell, um die Lebensdauer von Haushaltsgeräten zu verlängern, Energie zu sparen und die Hygiene zu gewährleisten. Besonders Geräte, die Wasser erhitzen, sind anfällig für Kalkablagerungen."
                )}
              </p>
              <ul>
                <li>{t("faq.cat5.q3.li1", "Wasserkocher")}</li>
                <li>
                  {t("faq.cat5.q3.li2", "Kaffeemaschine / Kaffeevollautomat")}
                </li>
                <li>{t("faq.cat5.q3.li3", "Waschmaschine")}</li>
                <li>{t("faq.cat5.q3.li4", "Geschirrspüler")}</li>
                <li>{t("faq.cat5.q3.li5", "Dampfbügeleisen / Dampfgarer")}</li>
                <li>{t("faq.cat5.q3.li6", "Espressomaschine (Siebträger)")}</li>
                <li>{t("faq.cat5.q3.li7", "Heißwasserspender / Boiler")}</li>
              </ul>
              <h4>{t("faq.cat5.q3.subtitle1", "Woher kommt der Kalk?")}</h4>
              <p>
                {t(
                  "faq.cat5.q3.text2",
                  "Kalk (Calciumkarbonat) ist ein natürlicher Bestandteil des Trinkwassers. Niederschlag versickert im Boden und löst auf dem Weg durch Gesteinsschichten (wie Kalkstein) Mineralien heraus. Wenn Wasser erhitzt wird, verdampft Kohlendioxid und das gelöste Calciumkarbonat fällt als feste weiße Schicht aus. Kalk wirkt als Isolator auf Heizelementen, was den Energieverbrauch erhöht. Als Entkalker eignen sich Essig, Zitronensäure oder spezielle Maschinenentkalker."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>
        </Card>

        {/* Kategorie 6: Wasserzähler */}
        <Card>
          <h2>{t("faq.cat6.title", "Wasserzähler")}</h2>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat6.q1.title",
                "Die digitale Technik ist mir zu kompliziert. Warum bauen Sie neue Wasserzähler ein?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat6.q1.text1",
                  "Es ist verständlich, dass neue Wasserzähler zunächst irritierend wirken. Die modernen Zähler sind jedoch darauf ausgelegt, den Prozess langfristig komfortabler und genauer zu gestalten. Übermittelt werden die Zählernummer, der Zählerstand, der Durchfluss, die Wasser- und Umgebungstemperatur sowie eventuelle Fehlermeldungen."
                )}
              </p>
              <h4>
                {t(
                  "faq.cat6.q1.subtitle1",
                  "Warum wurden diese Wasserzähler eingebaut?"
                )}
              </h4>
              <ul>
                <li>
                  {t(
                    "faq.cat6.q1.li1",
                    "Gesetzliche Pflicht: Laut dem Mess- und Eichgesetz müssen Wasserzähler alle sechs Jahre ausgetauscht werden."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat6.q1.li2",
                    "Moderne Technologie: Es handelt sich um digitale Funkwasserzähler (oft Ultraschall), die den Verbrauch genauer messen."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat6.q1.li3",
                    "Fernauslesung: Die Daten werden per Funk gesendet – kein Ableser muss mehr in Ihre Wohnung kommen."
                  )}
                </li>
              </ul>
              <h4>
                {t("faq.cat6.q1.subtitle2", "Was soll damit erreicht werden?")}
              </h4>
              <ul>
                <li>
                  {t(
                    "faq.cat6.q1.li4",
                    "Komfort: Die jährliche manuelle Ablesung entfällt."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat6.q1.li5",
                    "Genauigkeit: Die Ultraschallmessung verhindert Fehler durch Verschleiß."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat6.q1.li6",
                    "Leckageerkennung: Viele Zähler erkennen dauerhaft fließendes Wasser und helfen, Wasserverluste und hohe Kosten zu vermeiden."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat6.q1.li7",
                    "Datenschutz und Effizienz: Die Datenübertragung ist sicher, und der Versorger erhält präzise Verbrauchsdaten."
                  )}
                </li>
              </ul>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat6.q2.title",
                "Welche Vorteile habe ich als Kunde von den neuen digitalen Messgeräten?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat6.q2.text1",
                  "Ein wichtiger Baustein für nachhaltige Wasserversorgung ist der Einsatz digitaler Wasserzähler. Diese ermöglichen es, Wasserverluste – etwa durch Rohrbrüche oder Leckagen im Versorgungsnetz – noch schneller zu erkennen und gezielt gegenzusteuern."
                )}
              </p>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat6.q2.text2",
                    "Zusammengefasst ergeben sich folgende Vorteile: [10]"
                  )
                )}
              </p>
              <ul>
                <li>
                  {t("faq.cat6.q2.li1", "Höhere Genauigkeit der Messung")}
                </li>
                <li>
                  {t(
                    "faq.cat6.q2.li2",
                    "Keine Schätzungen des Wasserverbrauchs mehr"
                  )}
                </li>
                <li>{t("faq.cat6.q2.li3", "Langfristig kostengünstiger")}</li>
                <li>
                  {t(
                    "faq.cat6.q2.li4",
                    "Warnungen und Fehlermeldungen können ausgelesen werden"
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat6.q2.li5",
                    "Der Kunde muss den Zählerstand nicht mehr ablesen und mitteilen"
                  )}
                </li>
              </ul>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat6.q3.title",
                "Hat die elektromagnetische Strahlung des neuen digitalen Wasserzählers gesundheitliche Folgen?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat6.q3.text1",
                  "Nein! Die Strahlenbelastung durch digitale Zähler ist für den menschlichen Körper völlig unbedenklich und nicht wahrnehmbar. Digitale Wasserzähler entsprechen den einschlägigen nationalen und internationalen (WHO) Vorschriften und Normen für Elektromagnetische Umweltverträglichkeit (EMV)."
                )}
              </p>
              <p>
                {t(
                  "faq.cat6.q3.text2",
                  "Verglichen mit einem Handy weist der digitale Zähler eine 200-fach geringere Leistung auf. Zudem befinden sich Verbraucherzähler im Durchschnitt mindestens 15 m vom Menschen entfernt. Die Strahlenbelastung eines 30-minütigen Telefonats wird von Verbraucherzählern erst nach vielen Jahren erreicht."
                )}
              </p>
              <p>
                {t(
                  "faq.cat6.q3.text3",
                  "Auch auf die Trinkwasserqualität haben die Zähler keinen Einfluss, denn zur Herstellung werden ausschließlich trinkwasserzugelassene Materialien verwendet."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>
        </Card>

        {/* Kategorie 7: Allgemeine Fragen */}
        <Card>
          <h2>{t("faq.cat7.title", "Allgemeine Fragen")}</h2>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat7.q1.title",
                "Wo kommt das Trinkwasser her? Wie wird es aufbereitet?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat7.q1.text1",
                    "Zunächst wird das sogenannte Rohwasser gewonnen, das aus Grundwasserleitern, Oberflächengewässern (z. B. Trinkwassertalsperren) oder aus natürlichen Quellen entstammt. In der Regel wird Trinkwasser vor Ort gewonnen, um es auf kürzestem Weg zum Verbraucher zu bringen. Der örtliche Wasserversorger sorgt dafür, dass aus Rohwasser Trinkwasser wird. [12]"
                  )
                )}
              </p>
              <h4>
                {t(
                  "faq.cat7.q1.subtitle1",
                  "Die Wasseraufbereitung erfolgt in mehreren Schritten"
                )}
              </h4>
              <ul>
                <li>
                  {t(
                    "faq.cat7.q1.li1",
                    "Entnahme: Wasser wird aus Oberflächen- oder Grundwasserquellen entnommen."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat7.q1.li2",
                    "Vorbehandlung: Grobe Verunreinigungen werden durch Sieb- und Sedimentationsverfahren entfernt."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat7.q1.li3",
                    "Chemische Aufbereitung: Flockungsmittel werden hinzugefügt, um weitere Verunreinigungen zu binden."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat7.q1.li4",
                    "Filtration: Das Wasser wird durch verschiedene Filter (z. B. Sandfilter) geleitet."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat7.q1.li5",
                    "Desinfektion: Das Wasser wird häufig mit Chlor oder UV-Licht desinfiziert."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat7.q1.li6",
                    "Qualitätskontrolle: Unabhängige Labore führen regelmäßige Analysen durch."
                  )}
                </li>
              </ul>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat7.q2.title",
                "Kann ich alte Arzneimittel in der Toilette entsorgen?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat7.q2.text1",
                  "Auf keinen Fall! Medikamente gehören nicht in die Toilette oder den Ausguss. Das gleiche gilt für Farbreste und wassergefährdende Chemikalien. Damit möglichst wenig davon in den Wasserkreislauf gelangt, kann jeder Einzelne einen wichtigen Beitrag leisten: Medikamente sorgsam verwenden und richtig entsorgen – z. B. in der Apotheke. Diese nehmen nicht gebrauchte Arzneimittel kostenfrei zurück."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat7.q3.title",
                "Warum sind Wasserpreise überall so unterschiedlich?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat7.q3.text1",
                  "Jedes Wasserwerk findet unterschiedliche Bedingungen vor, was das lokale Rohwasser angeht. Daraus ergibt sich, in wie vielen und welchen Schritten es aufbereitet werden muss. Das ist der erste große Kostenfaktor."
                )}
              </p>
              <p>
                {t(
                  "faq.cat7.q3.text2",
                  "Der zweite wichtige Grund sind die geografischen Gegebenheiten vor Ort. Müssen die Wasserleitungen durch steiniges oder lehmiges Gebiet verlegt und gewartet werden, verursacht das höhere Kosten als bei lockerem sandigen Boden. Auch wenn Höhenunterschiede überwunden werden müssen, braucht es Pumpen und Wasserhochbehälter für gleichbleibenden Wasserdruck."
                )}
              </p>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat7.q4.title",
                "Haben wir genug Wasser (in einem trockenen Sommer)?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat7.q4.text1",
                    "Ja, Deutschland hat derzeit genug Trinkwasser und gehört zu den wasserreichen Ländern. Es gibt keinen flächendeckenden Mangel, und die Qualität ist durch strenge Kontrollen sehr hoch. Dennoch führen Klimawandel, Trockenheit und sinkende Grundwasserspiegel lokal zu Engpässen. [13]"
                  )
                )}
              </p>
              <ul>
                <li>
                  {t(
                    "faq.cat7.q4.li1",
                    "Versorgungslage: Rund 70 % des Trinkwassers werden aus Grund- und Quellwasser gewonnen. Auch in trockenen Sommern ist die Wasserversorgung für Haushalte bisher nicht flächendeckend gefährdet."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat7.q4.li2",
                    "Regionale Unterschiede: In einigen Regionen, insbesondere im Nordosten, sinken die Grundwasserstände."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat7.q4.li3",
                    "Klimawandel-Auswirkungen: Die Verteilung über das Jahr ändert sich durch Dürreperioden. Die Herausforderung besteht darin, das Wasser zur richtigen Zeit am richtigen Ort zu haben."
                  )}
                </li>
                <li>
                  {t(
                    "faq.cat7.q4.li4",
                    "Verbrauch: Der tägliche Pro-Kopf-Verbrauch liegt bei etwa 121 Litern."
                  )}
                </li>
              </ul>
            </FAQAnswer>
          </FAQItem>

          <FAQItem>
            <FAQQuestion>
              {t(
                "faq.cat7.q5.title",
                "Beim Netzspülen verschwendet der Versorger Trinkwasser. Kann das aufgefangen und genutzt werden?"
              )}
            </FAQQuestion>
            <FAQAnswer>
              <p>
                {t(
                  "faq.cat7.q5.text1",
                  "Beim Spülen der Rohre werden Ablagerungen, Sedimente (Sand, Rost) und Biofilme aus dem Leitungsnetz entfernt. Das Wasser ist daher zu Beginn meist trüb oder rostbraun."
                )}
              </p>
              <p>
                {t(
                  "faq.cat7.q5.text2",
                  "Das Spülwasser kann in Tonnen aufgefangen werden. Nachdem sich die Schwebstoffe abgesetzt haben, eignet es sich sehr gut zum Gießen von Pflanzen im Garten."
                )}
              </p>
              <p>
                {processInlineFormatting(
                  t(
                    "faq.cat7.q5.text3",
                    "Vom Tränken von Tieren wird allerdings abgeraten. Das Spülwasser enthält gelöste Partikel aus dem Biofilm, die bei Tieren zu Magen-Darmerkrankungen oder einem geschwächten Immunsystem führen können. Wenn Sie das Wasser auffangen dürfen (nach Rücksprache mit dem Versorger), ist der Einsatz im Garten sinnvoll. Zum Trinken ist es nicht geeignet. [14]"
                  )
                )}
              </p>
            </FAQAnswer>
          </FAQItem>
        </Card>

        <SourcesSection>
          <h3>{t("faq.literatur.title", "Quellenverzeichnis")}</h3>
          <ol>
            <li id="quelle-1">
              <LinkifyText>
                {t(
                  "faq.literatur.ref1",
                  "Umweltbundesamt (2020): Trinkwasser – Ratgeber für Verbraucherinnen und Verbraucher. https://www.umweltbundesamt.de/system/files/medien/421/publikationen/uba_trinkwas-ratgeber_2020-04-07_web_barrierefrei.pdf"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-2">
              <LinkifyText>
                {t(
                  "faq.literatur.ref2",
                  "WVR Wasserblog: Unerwartet erhöhter Wasserverbrauch. https://www.wvr.de/wasserblog2/unerwartet-erhoehter-wasserverbrauch"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-3">
              <LinkifyText>
                {t(
                  "faq.literatur.ref3",
                  "Berliner Wasserbetriebe: Fragen und Antworten. https://www.bwb.de/de/fragen-und-antworten.php"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-4">
              <LinkifyText>
                {t(
                  "faq.literatur.ref4",
                  "Veolia Deutschland: Fragen und Antworten. https://service.veolia.de/fragen-und-antworten/"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-5">
              <LinkifyText>
                {t(
                  "faq.literatur.ref5",
                  "Stadtwerke Hilden: FAQ Wasser. https://stadtwerke-hilden.de/faq/wasser/"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-6">
              <LinkifyText>
                {t(
                  "faq.literatur.ref6",
                  "Umweltbundesamt: Rund um das Trinkwasser – Ratgeber. https://www.umweltbundesamt.de/system/files/medien/479/publikationen/uba_rund_um_das_trinkwasser_ratgeber_web_0.pdf"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-7">
              <LinkifyText>
                {t(
                  "faq.literatur.ref7",
                  "Verbraucherzentrale: Kann man Leitungswasser bedenkenlos trinken? https://www.verbraucherzentrale.de/wissen/umwelt-haushalt/kann-man-leitungswasser-bedenkenlos-trinken-112100"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-8">
              <LinkifyText>
                {t(
                  "faq.literatur.ref8",
                  "Bundesgesundheitsministerium: Trinkwasser. https://www.bundesgesundheitsministerium.de/service/begriffe-von-a-z/t/trinkwasser/seite"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-9">
              <LinkifyText>
                {t(
                  "faq.literatur.ref9",
                  "MVV Energie: FAQ – Fragen und Antworten Wasser. https://www.mvv.de/wasser/faq-fragen-antworten"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-10">
              <LinkifyText>
                {t(
                  "faq.literatur.ref10",
                  "Stadtwerke Schrobenhausen: Fragen zur Einführung des digitalen Wasserzählers. https://www.stadtwerke-sob.de/files/STW/Formulare/Trinkwasser/Fragen%20zur%20Einf%C3%BChrung%20des%20digitalen%20Wasserz%C3%A4hlers.pdf"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-11">
              <LinkifyText>
                {t(
                  "faq.literatur.ref11",
                  "Verbraucherzentrale Hamburg: Betrug mit Wasser. https://www.vzhh.de/themen/umwelt-nachhaltigkeit/trinkwasser/betrug-wasser"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-12">
              <LinkifyText>
                {t(
                  "faq.literatur.ref12",
                  "Stadtwerke Bad Nauheim: Wasserversorgung. https://www.stadtwerke-bad-nauheim.de/wasserversorgung/energiespartipps-wasserverbrauch"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-13">
              <LinkifyText>
                {t(
                  "faq.literatur.ref13",
                  "Umweltbundesamt: Trockenheit in Deutschland – Fragen und Antworten. https://www.umweltbundesamt.de/themen/wasser/extremereignisseklimawandel/trockenheit-in-deutschland-fragen-antworten"
                )}
              </LinkifyText>
            </li>
            <li id="quelle-14">
              <LinkifyText>
                {t(
                  "faq.literatur.ref14",
                  "Umweltbundesamt. https://www.umweltbundesamt.de"
                )}
              </LinkifyText>
            </li>
          </ol>
        </SourcesSection>
      </ContentContainer>
    </AppLayout>
  );
});
